"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { createPortal } from "react-dom"
import { ImageIcon, Trash2, RotateCw, Maximize2, RefreshCw, ChevronLeft, Plus, MousePointer, QrCode, Smartphone, CheckCircle2, X, Sparkles, Crop } from "lucide-react"
import { cn } from "../../../lib/utils"
import { generateSessionId, mobileUploadUrl, pollSessionLogo } from "./logo-session"

// URL do backend (Express, onde fica a rota /api/remove-bg) — mesmo padrão
// já usado em outra parte do projeto (a função "download" que converte
// vídeo), pra manter consistência.
const BACKEND_URL =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:4000" // backend roda em HTTP puro — Chrome permite isso especificamente pra localhost, mesmo com frontend em HTTPS
    : "https://simuladorksports.com/aisten-kings2-backend"

// ─── Types ────────────────────────────────────────────────────────────────────

interface LogoItem {
  index: number   // stable index into window.arrayGroupLogos
  dataURL: string
}

// ─── Read logos from window ───────────────────────────────────────────────────

function readWindowLogos(): LogoItem[] {
  const arr: any[] = (window as any).arrayGroupLogos ?? []
  const items: LogoItem[] = []
  arr.forEach((obj, i) => {
    if (!obj) return // slot was deleted but index kept — skip nulls
    // Uploaded logos are fabric.Image (have _element.src); logos that came
    // from the SVG itself are fabric.Group (no _element) — render those via
    // toDataURL so their thumbnails show up in the grid.
    const src =
      obj._element?.src ??
      obj._element?.currentSrc ??
      (typeof obj.toDataURL === "function" ? obj.toDataURL({ format: "png", multiplier: 2 }) : "")
    items.push({ index: i, dataURL: src })
  })
  return items
}

// ─── Make a fake jQuery-like object LogoEdit.js expects as currentLogo ────────
// deleteLogo() calls currentLogo.data("index") and currentLogo.remove()
// ReplaceIframeLogo calls currentLogo.data("index") and currentLogo.find("div")

function makeFakeCurrentLogo(index: number, onRemove: () => void) {
  return {
    data: (key: string) => key === "index" ? index : undefined,
    remove: () => onRemove(),
    find: () => ({ css: () => {} }), // noop for thumbnail update — React handles it
  }
}

// ─── Waiting for click indicator ──────────────────────────────────────────────

function WaitingForClick({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-primary/60 bg-primary/5 p-4 flex flex-col items-center gap-3 text-center animate-in fade-in duration-200">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
        <MousePointer className="w-5 h-5 text-primary" />
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">Clique no uniforme</p>
        <p className="text-xs text-muted-foreground mt-0.5">Toque onde quer posicionar a logo</p>
      </div>
      <button
        onClick={onCancel}
        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
      >
        Cancelar
      </button>
    </div>
  )
}

// ─── Logo thumbnail card ──────────────────────────────────────────────────────

function LogoCard({ item, onEdit }: { item: LogoItem; onEdit: (item: LogoItem) => void }) {
  return (
    <button
      onClick={() => onEdit(item)}
      className="group relative w-full aspect-square rounded-2xl border-2 border-border/40 hover:border-primary/60 bg-secondary/20 overflow-hidden transition-all duration-150 hover:scale-[1.03] active:scale-95 touch-manipulation"
    >
      {item.dataURL ? (
        <img src={item.dataURL} alt="logo" className="w-full h-full object-contain p-2" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="w-6 h-6 text-muted-foreground/40" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <span className="text-white text-xs font-semibold">Editar</span>
      </div>
    </button>
  )
}

// ─── Edit panel ───────────────────────────────────────────────────────────────

// ── Cortar imagem ────────────────────────────────────────────────────────────
// Overlay de tela cheia com um retângulo arrastável/redimensionável sobre a
// imagem — sem depender de nenhuma lib externa. Especialmente útil pra fotos
// tiradas com o celular (via QR code), que costumam vir com uma área bem
// maior do que o usuário realmente quer usar como logo.
function clampPct(v: number, min: number, max: number) {
  return Math.min(Math.max(v, min), max)
}

function CropModal({
  imageSrc,
  onCancel,
  onConfirm,
}: {
  imageSrc: string
  onCancel: () => void
  onConfirm: (dataUrl: string) => void
}) {
  const imgRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [crop, setCrop] = useState({ x: 10, y: 10, w: 80, h: 80 }) // tudo em % da imagem exibida
  const [dragMode, setDragMode] = useState<null | "move" | "nw" | "ne" | "sw" | "se">(null)
  const dragStartRef = useRef({ clientX: 0, clientY: 0, crop: { x: 0, y: 0, w: 0, h: 0 } })

  const startDrag = (mode: typeof dragMode, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const point = "touches" in e ? e.touches[0] : (e as React.MouseEvent)
    dragStartRef.current = { clientX: point.clientX, clientY: point.clientY, crop: { ...crop } }
    setDragMode(mode)
  }

  useEffect(() => {
    if (!dragMode) return

    const onMove = (e: MouseEvent | TouchEvent) => {
      const point = "touches" in e ? e.touches[0] : e
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect || !point) return

      const dxPct = ((point.clientX - dragStartRef.current.clientX) / rect.width) * 100
      const dyPct = ((point.clientY - dragStartRef.current.clientY) / rect.height) * 100
      const start = dragStartRef.current.crop
      let next = { ...start }

      if (dragMode === "move") {
        next.x = clampPct(start.x + dxPct, 0, 100 - start.w)
        next.y = clampPct(start.y + dyPct, 0, 100 - start.h)
      } else {
        if (dragMode.includes("n")) {
          const newY = clampPct(start.y + dyPct, 0, start.y + start.h - 5)
          next.h = start.h - (newY - start.y)
          next.y = newY
        }
        if (dragMode.includes("s")) {
          next.h = clampPct(start.h + dyPct, 5, 100 - start.y)
        }
        if (dragMode.includes("w")) {
          const newX = clampPct(start.x + dxPct, 0, start.x + start.w - 5)
          next.w = start.w - (newX - start.x)
          next.x = newX
        }
        if (dragMode.includes("e")) {
          next.w = clampPct(start.w + dxPct, 5, 100 - start.x)
        }
      }
      setCrop(next)
    }

    const onUp = () => setDragMode(null)

    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
    window.addEventListener("touchmove", onMove, { passive: false })
    window.addEventListener("touchend", onUp)
    return () => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
      window.removeEventListener("touchmove", onMove)
      window.removeEventListener("touchend", onUp)
    }
  }, [dragMode])

  const handleConfirm = () => {
    const img = imgRef.current
    if (!img) return

    const sx = (crop.x / 100) * img.naturalWidth
    const sy = (crop.y / 100) * img.naturalHeight
    const sw = (crop.w / 100) * img.naturalWidth
    const sh = (crop.h / 100) * img.naturalHeight

    const canvas = document.createElement("canvas")
    canvas.width = Math.max(1, Math.round(sw))
    canvas.height = Math.max(1, Math.round(sh))
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)
    onConfirm(canvas.toDataURL("image/png"))
  }

  const corners: Array<"nw" | "ne" | "sw" | "se"> = ["nw", "ne", "sw", "se"]

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 2147483000,
        background: "rgba(0,0,0,.88)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 16, gap: 16,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", textAlign: "center" }}>
        Arraste as bordas ou o centro pra ajustar o corte
      </div>

      <div
        ref={containerRef}
        style={{ position: "relative", display: "inline-block", maxWidth: "90vw", maxHeight: "62vh" }}
      >
        <img
          ref={imgRef}
          src={imageSrc}
          crossOrigin="anonymous"
          draggable={false}
          style={{ display: "block", maxWidth: "90vw", maxHeight: "62vh", userSelect: "none" }}
        />
        <div
          onMouseDown={(e) => startDrag("move", e)}
          onTouchStart={(e) => startDrag("move", e)}
          style={{
            position: "absolute",
            left: `${crop.x}%`, top: `${crop.y}%`, width: `${crop.w}%`, height: `${crop.h}%`,
            border: "2px solid #fff",
            boxShadow: "0 0 0 9999px rgba(0,0,0,.62)",
            cursor: "move",
            touchAction: "none",
          }}
        >
          {corners.map((corner) => (
            <div
              key={corner}
              onMouseDown={(e) => startDrag(corner, e)}
              onTouchStart={(e) => startDrag(corner, e)}
              style={{
                position: "absolute",
                width: 20, height: 20, background: "#fff", borderRadius: 6,
                cursor: corner === "nw" || corner === "se" ? "nwse-resize" : "nesw-resize",
                top: corner.includes("n") ? -10 : undefined,
                bottom: corner.includes("s") ? -10 : undefined,
                left: corner.includes("w") ? -10 : undefined,
                right: corner.includes("e") ? -10 : undefined,
                touchAction: "none",
              }}
            />
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={handleConfirm}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "#fff", color: "#111", fontWeight: 700, fontSize: 13,
            padding: "10px 20px", borderRadius: 12,
          }}
        >
          <Crop className="w-4 h-4" />
          Aplicar corte
        </button>
        <button
          onClick={onCancel}
          style={{
            background: "rgba(255,255,255,.12)", color: "#fff", fontSize: 13,
            padding: "10px 20px", borderRadius: 12,
          }}
        >
          Cancelar
        </button>
      </div>
    </div>,
    document.body
  )
}

function LogoEditPanel({
  item,
  onBack,
  onDeleted,
  onReplaced,
}: {
  item: LogoItem
  onBack: () => void
  onDeleted: () => void
  onReplaced: (newDataURL: string) => void
}) {
  const w = window as any
  const obj = w.arrayGroupLogos?.[item.index]

  const initialScale = (() => {
    if (!obj) return 50
    const MIN_H = 50, MAX_H = 1500
    const t = ((obj.height * obj.scaleY) - MIN_H) / (MAX_H - MIN_H)
    return Math.round(Math.max(0, Math.min(1, t)) * 100)
  })()

  const [scale, setScale] = useState(initialScale)
  const [angle, setAngle] = useState(Math.round(obj?.angle ?? 0))
  const [thumbURL, setThumbURL] = useState(item.dataURL)
  const [removingBg, setRemovingBg] = useState(false)
  const [isCropOpen, setIsCropOpen] = useState(false)
  const [removeBgError, setRemoveBgError] = useState<string | null>(null)
  const replaceRef = useRef<HTMLInputElement>(null)

  // Set targetImage + editingImage so the raycast in Input.js works for dragging
  useEffect(() => {
    if (!obj) return
    w.targetImage = obj
    w.editingImage = true
    w["fabricRes"]?.("low")

    return () => {
      // cleaned up in handleBack / handleDelete
    }
  }, [obj])

  const exitEdit = () => {
    w.editingImage = false
    w.targetImage = null
    w["fabricRes"]?.("high")
    w["setupCamera"]?.()
    w.cube && (w.cube.visible = false)
  }

  const handleBack = () => {
    exitEdit()
    onBack()
  }

  const handleScale = (v: number) => {
    setScale(v)
    w["scale-logo-iframe"]?.(v)
  }

  const handleAngle = (v: number) => {
    setAngle(v)
    w["rotate-logo-iframe"]?.(v)
  }

  // ── Delete ──────────────────────────────────────────────────────────────────
  // Mirrors deleteLogo(true) from LogoEdit.js, but nulls the slot instead of
  // splice so other indices stay stable, then fires event for React to re-read.
  const handleDelete = () => {
    if (!obj) return

    if (obj.group) obj.group.removeWithUpdate(obj)
    w.fabricCanvas?.remove(obj)
    w.fabricCanvas?.renderAll()
    w["updateCanvas"]?.()

    // Null the slot so readWindowLogos skips it
    w.arrayGroupLogos[item.index] = null

    exitEdit()

    // Notify React
    window.dispatchEvent(new Event("logosChanged"))
    onDeleted()
  }

  // ── Substitui a imagem do fabric no lugar, mantendo posição/tamanho/ângulo ──
  // Compartilhada entre "Trocar imagem" (arquivo escolhido manualmente) e
  // "Remover fundo com IA" (imagem devolvida pela API).
  const applyReplacementImage = (imageUrl: string) => {
    const position = w.arrayGroupLogos?.[item.index]
    if (!position) return

    let center: { x: number; y: number }
    try { center = position.getCenterPoint() }
    catch { center = { x: position.left, y: position.top } }

    const { fabric } = w
    fabric.Image.fromURL(imageUrl, (img: any) => {
      const scaleX = (position.width * position.scaleX) / img.width
      const scaleY = (position.height * position.scaleY) / img.height
      const scale = Math.min(scaleX, scaleY)

      img.new_id = Math.random()
      img.set({
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
        scaleX: scale,
        scaleY: scale,
        angle: position.angle || 0,
        iniScale: scale,
      })

      // Remove old, add new
      if (position.group) position.group.removeWithUpdate(position)
      w.fabricCanvas?.remove(position)
      w.fabricCanvas?.add(img)
      w.fabricCanvas?.renderAll()
      w["updateCanvas"]?.()

      // Update the slot in-place so indices stay stable
      w.arrayGroupLogos[item.index] = img

      // Update targetImage to the new object so dragging still works
      w.targetImage = img

      // Update thumbnail in panel
      setThumbURL(imageUrl)

      onReplaced(imageUrl)
    }, { crossOrigin: "anonymous" })
  }

  // ── Replace ─────────────────────────────────────────────────────────────────
  // Mirrors .replace-iframe-logo change handler from LogoEdit.js
  const handleReplaceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !file.type.startsWith("image/")) return

    const reader = new FileReader()
    reader.onload = (ev) => {
      const imageUrl = ev.target?.result as string
      applyReplacementImage(imageUrl)
      w['toast']?.("Você pode reposicionar a imagem arrastando pelo modelo 3D!", "info")
    }
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  // ── Remover fundo com IA ────────────────────────────────────────────────────
  // Manda a imagem atual da logo pro nosso backend (que repassa pra
  // MantosControl API com o token guardado no servidor) e substitui pelo
  // resultado com fundo transparente, mantendo posição/tamanho.
  const handleRemoveBackground = async () => {
    setRemovingBg(true)
    setRemoveBgError(null)


    try {
      const sourceBlob = await (await fetch(thumbURL)).blob()

      const formData = new FormData()
      formData.append("image", sourceBlob, "logo.png")

      const targetUrl = `${BACKEND_URL}/api/remove-bg`

      const res = await fetch(targetUrl, { method: "POST", body: formData })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data?.message || "Não foi possível remover o fundo.")
      }

      // O backend já devolve a imagem pronta em base64 (dataUrl) — não
      // precisamos mais buscar nada direto de mantosfc.com no navegador
      // (isso era bloqueado por CORS/autenticação).
      applyReplacementImage(data.dataUrl)
      w['toast']?.("Fundo removido! Reposicione se precisar.", "info")
    } catch (err: any) {
      console.error("[LogoEditPanel] erro ao remover fundo:", err)
      console.error("[LogoEditPanel] detalhes — name:", err?.name, "| message:", err?.message, "| cause:", err?.cause, "| stack:", err?.stack)
      setRemoveBgError(err?.message || "Erro ao remover o fundo. Tente novamente.")
    } finally {
      setRemovingBg(false)
    }
  }

  return (
    <div className="space-y-4 animate-in slide-in-from-right-4 duration-200">

      {/* Header */}
      <div className="flex items-center gap-2">
        <button onClick={handleBack}
          className="w-8 h-8 rounded-xl bg-secondary/60 hover:bg-secondary active:scale-95 flex items-center justify-center transition-all touch-manipulation flex-shrink-0">
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <span className="text-sm font-semibold text-foreground">Editar Logo</span>
        {thumbURL && (
          <div className="ml-auto w-10 h-10 rounded-xl border border-border/40 bg-secondary/30 overflow-hidden flex-shrink-0">
            <img src={thumbURL} alt="logo" className="w-full h-full object-contain p-1" />
          </div>
        )}
      </div>

      {/* Scale */}
      <div className="space-y-2 rounded-2xl bg-secondary/20 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Maximize2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Tamanho</span>
          </div>
          <span className="text-xs font-mono text-muted-foreground">{scale}%</span>
        </div>
        <input type="range" min={0} max={100} value={scale}
          onChange={e => handleScale(Number(e.target.value))}
          className="w-full h-2 rounded-full accent-primary cursor-pointer touch-manipulation" />
      </div>

      {/* Rotation */}
      <div className="space-y-2 rounded-2xl bg-secondary/20 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RotateCw className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Rotação</span>
          </div>
          <span className="text-xs font-mono text-muted-foreground">{angle}°</span>
        </div>
        <input type="range" min={0} max={360} value={angle}
          onChange={e => handleAngle(Number(e.target.value))}
          className="w-full h-2 rounded-full accent-primary cursor-pointer touch-manipulation" />
      </div>

      {/* Replace */}
      <label className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl bg-secondary/20 hover:bg-secondary/40 border border-border/30 hover:border-primary/40 cursor-pointer transition-all touch-manipulation">
        <RefreshCw className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">Trocar imagem</p>
          <p className="text-xs text-muted-foreground">Mantém posição e tamanho</p>
        </div>
        <input ref={replaceRef} type="file" accept="image/*" className="hidden" onChange={handleReplaceFile} />
      </label>

      {/* Cortar imagem */}
      <button
        onClick={() => setIsCropOpen(true)}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl bg-secondary/20 hover:bg-secondary/40 border border-border/30 hover:border-primary/40 transition-all touch-manipulation"
      >
        <Crop className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-foreground">Cortar imagem</p>
          <p className="text-xs text-muted-foreground">Recorta a área que você quer usar</p>
        </div>
      </button>

      {isCropOpen && (
        <CropModal
          imageSrc={thumbURL}
          onCancel={() => setIsCropOpen(false)}
          onConfirm={(dataUrl) => {
            applyReplacementImage(dataUrl)
            setIsCropOpen(false)
            w['toast']?.("Imagem cortada! Reposicione se precisar.", "info")
          }}
        />
      )}

      {/* Remover fundo com IA */}
      <div className="space-y-1.5">
        <button
          onClick={handleRemoveBackground}
          disabled={removingBg}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/50 transition-all touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {removingBg ? (
            <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin flex-shrink-0" />
          ) : (
            <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
          )}
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-foreground">
              {removingBg ? "Removendo fundo…" : "Remover fundo com IA"}
            </p>
            <p className="text-xs text-muted-foreground">Deixa a imagem com fundo transparente</p>
          </div>
        </button>
        {removeBgError && (
          <p className="text-xs text-red-400 px-1">{removeBgError}</p>
        )}
      </div>

      {/* Delete */}
      <button onClick={handleDelete}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40 transition-all touch-manipulation">
        <Trash2 className="w-4 h-4 text-red-400 flex-shrink-0" />
        <span className="text-sm font-medium text-red-400">Remover logo</span>
      </button>

    </div>
  )
}

// ─── QR upload block (desktop only) ──────────────────────────────────────────

function QrUploadBlock({ onImageReady }: { onImageReady: (dataURL: string) => void }) {
  const [sessionId]  = useState(() => generateSessionId())
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<"waiting" | "received" | "expired">("waiting")
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const backToWaitingRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastNameRef = useRef<string | null>(null)
  const processingRef = useRef(false)
  const uploadUrl = mobileUploadUrl(sessionId)

  // Generate QR code image via a public API (no library needed)
  useEffect(() => {
    const encoded = encodeURIComponent(uploadUrl)
    setQrUrl(`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encoded}&bgcolor=ffffff&color=000000&margin=10`)
  }, [uploadUrl])

  // Poll Supabase every 3s — continua rodando indefinidamente (até expirar em
  // 10min), pra dar pra mandar mais de uma foto pelo mesmo QR code. Detecta
  // cada foto nova comparando o nome do arquivo com o último já processado —
  // o mobile agora sobe cada foto com um nome único (timestamp), então o
  // nome sozinho já é suficiente pra saber se é uma foto nova.
  useEffect(() => {
    let expired = false


    pollRef.current = setInterval(async () => {
      if (expired || processingRef.current) return

      const result = await pollSessionLogo(sessionId)
      if (!result) return
      if (result.name === lastNameRef.current) return // já processamos essa

      processingRef.current = true
      lastNameRef.current = result.name
      setStatus("received")
      if (backToWaitingRef.current) clearTimeout(backToWaitingRef.current)

      try {
        const res = await fetch(result.url)
        const blob = await res.blob()
        const reader = new FileReader()
        reader.onload = ev => {
          const dataURL = ev.target?.result as string
          onImageReady(dataURL)
          processingRef.current = false
        }
        reader.onerror = (err) => {
          console.error("[QrUploadBlock] erro no FileReader:", err)
          processingRef.current = false
        }
        reader.readAsDataURL(blob)
      } catch (err) {
        console.error("[QrUploadBlock] erro ao baixar/processar a foto:", err)
        processingRef.current = false
      }

      // Volta pra tela de "aguardando" depois de alguns segundos, pra dar
      // pra escanear/enviar outra foto pelo mesmo QR code.
      backToWaitingRef.current = setTimeout(() => {
        if (!expired) setStatus("waiting")
      }, 4000)
    }, 3000)

    // Expire after 10 minutes
    timeoutRef.current = setTimeout(() => {
      expired = true
      clearInterval(pollRef.current!)
      setStatus("expired")
    }, 10 * 60 * 1000)

    return () => {
      expired = true
      clearInterval(pollRef.current!)
      clearTimeout(timeoutRef.current!)
      if (backToWaitingRef.current) clearTimeout(backToWaitingRef.current)
    }
  }, [sessionId, onImageReady])

  if (status === "received") {
    return (
      <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-4 flex items-center gap-3 animate-in fade-in duration-300">
        <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-foreground">Logo recebida!</p>
          <p className="text-xs text-muted-foreground">Clique no uniforme para posicionar. Pode escanear de novo pra enviar outra.</p>
        </div>
      </div>
    )
  }

  if (status === "expired") {
    return (
      <div className="rounded-2xl border border-border/30 bg-secondary/20 p-3 text-center">
        <p className="text-xs text-muted-foreground">QR code expirado. Recarregue a página para gerar um novo.</p>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-border/30 bg-secondary/10 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Smartphone className="w-4 h-4 text-primary flex-shrink-0" />
        <p className="text-xs font-semibold text-foreground">Enviar do celular</p>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Escaneie o QR code com o celular para enviar a foto do escudo diretamente.
      </p>

      {/* QR code */}
      <div className="flex justify-center">
        {qrUrl ? (
          <div className="rounded-xl overflow-hidden border-4 border-white shadow-lg w-[140px] h-[140px]">
            <img src={qrUrl} alt="QR code" className="w-full h-full" />
          </div>
        ) : (
          <div className="w-[140px] h-[140px] rounded-xl bg-secondary/30 animate-pulse" />
        )}
      </div>

      {/* Waiting indicator */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
        Aguardando envio…
      </div>
    </div>
  )
}

// ─── Main LogoPicker component ────────────────────────────────────────────────

export function LogoPicker() {
  const [logos, setLogos] = useState<LogoItem[]>([])
  const [editing, setEditing] = useState<LogoItem | null>(null)
  const [waitingForClick, setWaitingForClick] = useState(false)
  const uploadRef = useRef<HTMLInputElement>(null)
  const prevLengthRef = useRef(0)

  const [isAiPromptOpen, setIsAiPromptOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState("")
  const [isGeneratingLogo, setIsGeneratingLogo] = useState(false)
  const [aiGenerateError, setAiGenerateError] = useState<string | null>(null)

  const refreshLogos = useCallback(() => {
    setLogos(readWindowLogos())
  }, [])

  useEffect(() => {
    refreshLogos()

    const onLayout = () => {
      setLogos([])
      setEditing(null)
      setWaitingForClick(false)
      prevLengthRef.current = 0
    }

    window.addEventListener("svgColorsReady", refreshLogos)
    window.addEventListener("layoutChanged", onLayout)
    window.addEventListener("logosChanged", refreshLogos)
    return () => {
      window.removeEventListener("svgColorsReady", refreshLogos)
      window.removeEventListener("layoutChanged", onLayout)
      window.removeEventListener("logosChanged", refreshLogos)
    }
  }, [refreshLogos])

  // Poll for new logo placement after user clicks on 3D model
  useEffect(() => {
    if (!waitingForClick) return

    const interval = setInterval(() => {
      const arr: any[] = (window as any).arrayGroupLogos ?? []
      const curr = arr.filter(Boolean).length
      if (curr > prevLengthRef.current) {
        prevLengthRef.current = curr
        setWaitingForClick(false)
        window.dispatchEvent(new Event("logoPlacementEnd"))
        refreshLogos()
      }
    }, 150)

    return () => clearInterval(interval)
  }, [waitingForClick, refreshLogos])

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const w = window as any
    const { fabric } = w
    if (!fabric) return

    const reader = new FileReader()
    reader.onload = (ev) => {
      const dataURL = ev.target?.result as string

      fabric.Image.fromURL(dataURL, (img: any) => {
        img.new_id = Math.random()
        img.iniScale = img.scaleX

        w.addingImage = true
        w.currentImage = img
        w.editingImage = true
        w.backLogo = true

        prevLengthRef.current = (w.arrayGroupLogos ?? []).filter(Boolean).length
        setWaitingForClick(true)
        window.dispatchEvent(new Event("logoPlacementStart"))
        w['toast']?.("Clique na roupa onde vc quer posicionar a imagem!", "info")
      })
    }
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  const handleCancelWaiting = () => {
    const w = window as any
    w.addingImage = false
    w.currentImage = null
    w.editingImage = false
    setWaitingForClick(false)
    window.dispatchEvent(new Event("logoPlacementEnd"))
  }

  const handleEdit = (item: LogoItem) => {
    const w = window as any
    const obj = w.arrayGroupLogos?.[item.index]
    if (!obj) return

    // Set targetImage so raycast drag works immediately
    w.targetImage = obj
    w.editingImage = true
    w["fabricRes"]?.("low")
    setEditing(item)
  }

  const handleDeleted = () => {
    setEditing(null)
    refreshLogos()
  }

  const handleReplaced = (newDataURL: string) => {
    // Update thumbnail in grid
    setLogos(prev => prev.map(l =>
      l.index === editing?.index ? { ...l, dataURL: newDataURL } : l
    ))
    // Stay in edit panel — user may want to keep adjusting
  }

  const handleImageReady = useCallback((dataURL: string) => {
    const w = window as any
    const { fabric } = w
    if (!fabric) {
      console.error("[LogoPicker] window.fabric não está disponível — abortando silenciosamente (esse é o bug!)")
      return
    }

    fabric.Image.fromURL(dataURL, (img: any) => {
      img.new_id = Math.random()
      img.iniScale = img.scaleX

      w.addingImage = true
      w.currentImage = img
      w.editingImage = true
      w.backLogo = true

      prevLengthRef.current = (w.arrayGroupLogos ?? []).filter(Boolean).length
      setWaitingForClick(true)
      window.dispatchEvent(new Event("logoPlacementStart"))
      w['toast']?.("Clique na roupa onde vc quer posicionar a imagem!", "info")
    })
  }, [])

  // ── Gerar logo por IA a partir de um prompt de texto ────────────────────────
  const handleGenerateLogo = useCallback(async () => {
    const prompt = aiPrompt.trim()
    if (!prompt) return

    setIsGeneratingLogo(true)
    setAiGenerateError(null)

    try {
      const res = await fetch(`${BACKEND_URL}/api/generate-logo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data?.message || "Não foi possível gerar a logo.")
      }

      // Mesma função usada pelo QR code — abre o "clique pra posicionar"
      handleImageReady(data.dataUrl)
      setIsAiPromptOpen(false)
      setAiPrompt("")
    } catch (err: any) {
      console.error("[LogoPicker] erro ao gerar logo por IA:", err)
      setAiGenerateError(err?.message || "Erro ao gerar a logo. Tente novamente.")
    } finally {
      setIsGeneratingLogo(false)
    }
  }, [aiPrompt, handleImageReady])

  return (
    <div className="space-y-5">

      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
          <ImageIcon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Logo</h3>
          <p className="text-xs text-muted-foreground">
            {editing ? "Ajuste a logo no uniforme"
              : logos.length > 0 ? "Clique em uma logo para editar"
              : "Adicione o escudo ou logo do time"}
          </p>
        </div>
      </div>

      {editing ? (
        <LogoEditPanel
          item={editing}
          onBack={() => setEditing(null)}
          onDeleted={handleDeleted}
          onReplaced={handleReplaced}
        />
      ) : (
        <div className="space-y-4">

          {waitingForClick && <WaitingForClick onCancel={handleCancelWaiting} />}

          {logos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {logos.map(item => (
                <LogoCard key={item.index} item={item} onEdit={handleEdit} />
              ))}
            </div>
          )}

          {logos.length === 0 && !waitingForClick && (
            <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
              <div className="w-14 h-14 rounded-2xl bg-secondary/40 flex items-center justify-center">
                <ImageIcon className="w-7 h-7 text-muted-foreground/50" />
              </div>
              <p className="text-sm text-muted-foreground max-w-[180px]">
                Nenhuma logo adicionada ainda
              </p>
            </div>
          )}

          {!waitingForClick && (
            <>
              {/* Upload from desktop */}
              <label className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border-2 border-dashed border-border/50 hover:border-primary/60 bg-secondary/10 hover:bg-primary/5 cursor-pointer transition-all touch-manipulation group">
                <Plus className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                <span className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors">
                  Adicionar logo
                </span>
                <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              </label>

              {/* Gerar logo por IA */}
              <div className="space-y-2">
                <button
                  onClick={() => setIsAiPromptOpen((v) => !v)}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/50 transition-all touch-manipulation"
                >
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">Adicionar Logo por IA</span>
                </button>

                {isAiPromptOpen && (
                  <div className="p-3 rounded-2xl bg-secondary/20 border border-border/30 space-y-2">
                    <textarea
                      autoFocus
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder='Descreva a logo que você quer, ex: "Logo minimalista de leão dourado"'
                      rows={3}
                      disabled={isGeneratingLogo}
                      className="w-full text-sm bg-secondary/40 border border-border/40 rounded-xl px-3 py-2 outline-none focus:border-primary/50 placeholder:text-muted-foreground/60 resize-none disabled:opacity-60"
                    />
                    {aiGenerateError && (
                      <p className="text-xs text-red-400">{aiGenerateError}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={handleGenerateLogo}
                        disabled={isGeneratingLogo || !aiPrompt.trim()}
                        className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold bg-primary text-primary-foreground rounded-xl py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isGeneratingLogo ? (
                          <>
                            <div className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                            Gerando…
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            Gerar
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => { setIsAiPromptOpen(false); setAiGenerateError(null) }}
                        disabled={isGeneratingLogo}
                        className="px-4 text-sm bg-secondary/40 hover:bg-secondary/70 rounded-xl disabled:opacity-50"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* QR code upload — desktop only. Fica montado o tempo todo (só
              escondido visualmente por CSS quando waitingForClick), pra não
              perder a sessão/QR a cada logo posicionada — antes, isso ficava
              dentro do mesmo bloco condicional acima e desmontava/remontava
              (gerando um QR novo) toda vez que uma logo era colocada. */}
          <div
            className="hidden lg:block"
            style={waitingForClick ? { display: "none" } : undefined}
          >
            <QrUploadBlock onImageReady={handleImageReady} />
          </div>

        </div>
      )}
    </div>
  )
}